import { Check } from 'lucide-react';
import { cn } from '@/shared/utils/helpers';
import type { OnboardingStep } from '../Types/Onboarding.types';

interface OnboardingSidebarProps {
  restaurantName: string;
  branchName: string;
  steps: OnboardingStep[];
  /** Highest step number the user has reached (1-based). Enables free traversal. */
  maxReachedStep: number;
  itemCount?: number;
  setupPath?: string | null;
  onNavigate?: (stepNumber: number) => void;
}

export const OnboardingSidebar = ({
  restaurantName,
  branchName,
  steps,
  maxReachedStep,
  itemCount = 0,
  setupPath,
  onNavigate
}: OnboardingSidebarProps) => {
  return (
    <aside className="flex h-full w-full flex-col border-r border-[#1f2422] bg-[#121514] px-5 py-8 text-white">
      <div className="mb-10">
        <span className="text-lg font-semibold tracking-tight text-white">
          Gio
        </span>
        {(restaurantName || branchName) && (
          <p className="mt-2 text-xs leading-relaxed text-[#858d89]">
            {restaurantName}
            {branchName ? ` — ${branchName}` : ''}
          </p>
        )}
      </div>

      <nav className="flex-1 space-y-1" aria-label="Onboarding steps">
        {steps.map((step, index) => {
          const stepNum = index + 1;
          const isActive = step.status === 'active';
          const isCompleted = step.status === 'done';
          const isReachable = stepNum <= maxReachedStep;
          const isClickable =
            Boolean(onNavigate) && isReachable && !isActive;
          const showCount =
            itemCount > 0 &&
            (step.id === 'items' ||
              step.label.toLowerCase().includes('item'));

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => {
                if (isClickable) onNavigate?.(stepNum);
              }}
              disabled={!isClickable && !isActive}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm font-medium transition-colors',
                isActive
                  ? 'bg-white/10 text-white'
                  : isReachable
                    ? 'cursor-pointer text-[#aab0ae] hover:bg-white/10 hover:text-white'
                    : 'cursor-default text-[#aab0ae]/40'
              )}
            >
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors',
                  isActive || isCompleted
                    ? 'bg-[#087f8c] text-white'
                    : isReachable
                      ? 'border border-white/40 text-[#aab0ae]'
                      : 'border border-white/20 text-[#aab0ae]/40'
                )}
              >
                {isCompleted && !isActive ? (
                  <Check className="h-3 w-3 stroke-[3]" />
                ) : (
                  stepNum
                )}
              </span>
              <span>{step.label}</span>
              {showCount && (
                <span className="ml-auto text-xs text-[#858d89]">
                  {itemCount}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="mt-auto text-xs leading-relaxed text-[#858d89]">
        Secure onboarding link
        <br />
        {setupPath || 'Open with your invite link to save'}
      </div>
    </aside>
  );
};
